import { motion } from 'framer-motion';
import { useState } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface FeedMediaProps {
    urls: string[];
}

export function FeedMedia({ urls }: FeedMediaProps) {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    if (urls.length === 0) return null;

    const renderGrid = () => {
        switch (urls.length) {
            case 1:
                return (
                    <div className="w-full">
                        <img
                            src={urls[0]}
                            className="w-full h-auto max-h-[512px] object-cover rounded-2xl border border-surface-200 dark:border-surface-800 cursor-zoom-in"
                            onClick={() => setSelectedImage(urls[0])}
                            alt="Feed"
                        />
                    </div>
                );
            case 2:
                return (
                    <div className="grid grid-cols-2 gap-2 h-64">
                        {urls.map((url, i) => (
                            <img
                                key={i}
                                src={url}
                                className="w-full h-full object-cover rounded-xl border border-surface-200 dark:border-surface-800 cursor-zoom-in"
                                onClick={() => setSelectedImage(url)}
                                alt={`Feed ${i}`}
                            />
                        ))}
                    </div>
                );
            case 3:
                return (
                    <div className="grid grid-cols-2 gap-2 h-64">
                        <img
                            src={urls[0]}
                            className="w-full h-full object-cover rounded-xl border border-surface-200 dark:border-surface-800 cursor-zoom-in"
                            onClick={() => setSelectedImage(urls[0])}
                            alt="Feed 0"
                        />
                        <div className="grid grid-rows-2 gap-2 h-full">
                            {urls.slice(1).map((url, i) => (
                                <img
                                    key={i}
                                    src={url}
                                    className="w-full h-full object-cover rounded-xl border border-surface-200 dark:border-surface-800 cursor-zoom-in"
                                    onClick={() => setSelectedImage(url)}
                                    alt={`Feed ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                );
            default: // 4+
                return (
                    <div className="grid grid-cols-2 gap-2 h-64">
                        {urls.slice(0, 4).map((url, i) => (
                            <img
                                key={i}
                                src={url}
                                className="w-full h-full object-cover rounded-xl border border-surface-200 dark:border-surface-800 cursor-zoom-in"
                                onClick={() => setSelectedImage(url)}
                                alt={`Feed ${i}`}
                            />
                        ))}
                    </div>
                );
        }
    };

    return (
        <div className="mt-3">
            {renderGrid()}

            {/* Lightbox */}
            {selectedImage && createPortal(
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 lg:p-10"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <motion.img
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        src={selectedImage}
                        className="max-w-full max-h-full object-contain rounded-lg"
                        alt="Full size"
                    />
                </motion.div>,
                document.body
            )}
        </div>
    );
}
